import React from 'react';
import styled from 'styled-components';
import { match } from 'react-router-dom';
import { Col, Modal } from 'antd';

import {
  SaveCommunicationError,
  SendCommunicationChannels,
  SendCommunicationSecondStepForm,
  SituationCategory,
} from 'types';
import { Constants } from 'core';
import ModalContentConfirm from 'components/organisms/modal-content-confirm';
import { useIntl } from 'common/config/i18n';
import ModalMsgsPreview from 'components/organisms/modal-msgs-preview';

export interface Props {
  match: match<Params>;
}

export interface Params {
  situationCategory?: SituationCategory;
}

export type ChannelsArePresentKeyType = 'mail' | 'sms' | 'whatsApp' | 'flashInfo';

export const VerticalSpace = styled.div`
  height: 40px;
`;

export const Wrapper = styled.div`
  padding: 32px;
  padding-top: 0;
`;

export const SelectionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 32px;
`;

export const ButtonWrapper = styled(Col)`
  display: flex;
  margin-top: 15px;
  justify-content: space-between;
  align-items: center;
`;

export const StickyBlock = styled.div<{ offsetTop?: number }>`
  position: sticky;
  top: ${({ offsetTop }) => offsetTop}px;
  z-index: 1;
`;

// INFO: sendrid is the only prohibited link as of 20/03/2025
const containsProhibitedLinks = (content: string): boolean => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const links = doc.querySelectorAll('a[href]');

  for (let i = 0; i < links.length; i++) {
    const href = links[i].getAttribute('href')?.toLowerCase();
    if (href && href.includes('.sendgrid')) {
      return true;
    }
  }

  return false;
};

export const isCommunicationContentHasProhibitedLinks = (values: SendCommunicationSecondStepForm): boolean => {
  if (!values?.communicationContent || values.communicationContent.length === 0) {
    return false;
  }

  const isSmsChannelSelected = values.communicationChannels?.includes(SendCommunicationChannels.Sms);

  return values.communicationContent.some((content) => {
    const messages = [
      content.compensationLongMessage,
      content.principalZoneLongMessage,
      content.secondaryZoneLongMessage,
    ];
    if (isSmsChannelSelected) {
      messages.push(content.principalZoneShortMessage);
      messages.push(content.secondaryZoneShortMessage);
    }

    const messagesToCheck = messages.filter(Boolean) as string[]; // Remove null/undefined values

    if (messagesToCheck.length === 0) return false;

    for (let i = 0; i < messagesToCheck.length; i++) {
      if (containsProhibitedLinks(messagesToCheck[i])) {
        return true;
      }
    }

    return false;
  });
};

export const isCommunicationContentHasPlaceholders = (values: SendCommunicationSecondStepForm): boolean => {
  if (!values?.communicationContent || values.communicationContent.length === 0) {
    return false;
  }

  // Helper function to remove href attributes before checking for placeholders
  const removeHrefAttributes = (text: string | undefined): string => {
    if (!text) return '';
    return text
      .replace(/href\s*=\s*"[^"]*"/gi, '') // Remove href="..."
      .replace(/href\s*=\s*'[^']*'/gi, ''); // Remove href='...'
  };

  const regexPattern = Constants.SEND_COMMUNCIATION_PLACEHOLDERS.map((word) => word).join('|');

  const regex = new RegExp(`\\b(${regexPattern})\\b`, 'gi');

  const isSmsChannelSelected = values.communicationChannels?.includes(SendCommunicationChannels.Sms);

  const communicationContentWithPlaceholders = values?.communicationContent?.find((content) => {
    const cleanCompensationLong = removeHrefAttributes(content.compensationLongMessage);
    const cleanPrincipalZoneLong = removeHrefAttributes(content.principalZoneLongMessage);
    const cleanPrincipalZoneShort = removeHrefAttributes(content.principalZoneShortMessage);
    const cleanSecondaryZoneLong = removeHrefAttributes(content.secondaryZoneLongMessage);
    const cleanSecondaryZoneShort = removeHrefAttributes(content.secondaryZoneShortMessage);

    return (
      cleanCompensationLong?.toLowerCase().match(regex) ||
      cleanCompensationLong?.toLowerCase().includes('xx') ||
      cleanPrincipalZoneLong?.toLowerCase().match(regex) ||
      cleanPrincipalZoneLong?.toLowerCase().includes('xx') ||
      (isSmsChannelSelected && cleanPrincipalZoneShort?.toLowerCase().match(regex)) ||
      (isSmsChannelSelected && cleanPrincipalZoneShort?.toLowerCase().includes('xx')) ||
      cleanSecondaryZoneLong?.toLowerCase().match(regex) ||
      cleanSecondaryZoneLong?.toLowerCase().includes('xx') ||
      (isSmsChannelSelected && cleanSecondaryZoneShort?.toLowerCase().includes('xx')) ||
      (isSmsChannelSelected && cleanSecondaryZoneShort?.toLowerCase().match(regex))
    );
  });

  return !!communicationContentWithPlaceholders;
};

export const formikInitialValues: SendCommunicationSecondStepForm = {
  situationId: undefined,
  reasonId: undefined,
  otherReasonComplementary: undefined,
  communicationChannels: [],
  mailObject: undefined,
  mailHeadband: undefined,
  communicationContent: [],
  signature: undefined,
  compensationSelected: false,
  isDifferedCommunication: false,
  differedDaysBeforeCirculation: undefined,
  differedCommunicationTime: undefined,
};

export const ConfirmCompensationModal = ({
  open,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  onCancel: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onConfirm: (isConfirm: boolean) => void;
}) => {
  const { t } = useIntl();

  return (
    <Modal
      footer={null}
      open={open}
      onCancel={onCancel}
      width={700}
      styles={{
        body: {
          height: '160px',
        },
      }}
      style={{ top: 285 }}
    >
      <ModalContentConfirm
        row
        title={<div>{t('sendCommunication', 'compensation', 'confirmModal', 'title')}</div>}
        onConfirm={onConfirm}
        labelConfirm={t('sendCommunication', 'compensation', 'confirmModal', 'btnConfirm')}
        labelCancel={t('sendCommunication', 'compensation', 'confirmModal', 'btnCancel')}
        btnCancelWidth="300px"
      />
    </Modal>
  );
};

export const MsgsPreviewModal = ({
  open,
  onCancel,
  onConfirm,
  isSaveCommunicationLoading,
  isBulkCommunication,
  saveCommunicationError,
}: {
  open: boolean;
  onCancel: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onConfirm: () => void;
  isSaveCommunicationLoading: boolean;
  isBulkCommunication: boolean;
  saveCommunicationError?: SaveCommunicationError;
}) => {
  return (
    <Modal
      footer={null}
      open={open}
      onCancel={onCancel}
      width={1400}
      styles={{
        body: {
          height: '940px',
          overflowY: 'scroll',
          padding: 5,
        },
      }}
    >
      <ModalMsgsPreview
        onConfirm={onConfirm}
        isSaveCommunicationLoading={isSaveCommunicationLoading}
        isBulkCommunication={!!isBulkCommunication}
        saveCommunicationError={saveCommunicationError}
      />
    </Modal>
  );
};

export const PlaceHolderModal = ({
  open,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  onCancel: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onConfirm: (isConfirm: boolean) => void;
}) => {
  const { t } = useIntl();

  return (
    <Modal
      footer={null}
      open={open}
      onCancel={onCancel}
      width={600}
      styles={{
        body: { height: '80px' },
      }}
      style={{ top: 400 }}
    >
      <ModalContentConfirm
        row
        title={<div>{t('sendCommunication', 'placeHolder', 'modal', 'title')}</div>}
        onConfirm={onConfirm}
        labelConfirm={t('sendCommunication', 'placeHolder', 'modal', 'btnConfirm')}
        labelCancel={t('sendCommunication', 'placeHolder', 'modal', 'btnCancel')}
        btnCancelWidth="300px"
      />
    </Modal>
  );
};

export const ProhibitedLinksModal = ({
  open,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  onCancel: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onConfirm: (isConfirm: boolean) => void;
}) => {
  const { t } = useIntl();

  return (
    <Modal
      footer={null}
      open={open}
      onCancel={onCancel}
      width={600}
      styles={{
        body: { height: '80px' },
      }}
      style={{ top: 400 }}
    >
      <ModalContentConfirm
        row
        title={<div>{t('sendCommunication', 'prohibitedLinks', 'modal', 'title')}</div>}
        onConfirm={onConfirm}
        labelConfirm={t('sendCommunication', 'prohibitedLinks', 'modal', 'btnConfirm')}
        labelCancel={t('sendCommunication', 'prohibitedLinks', 'modal', 'btnCancel')}
        btnCancelWidth="300px"
      />
    </Modal>
  );
};
